// Column model for grouped player stats tables.
//
// Each group has either a `label` (rendered as a spanning header above leaf cols)
// or no label (leaf-only). Leaf columns carry the keys read off PlayerStatsTotals
// / PlayerStatsAverages. Renderers below convert raw values into display strings
// for Totals, Averages, and per-Game rows.

const fmtMinutesFromTotal = (totalMinutes) => {
  if (totalMinutes === undefined || totalMinutes === null) return '-'
  const mm = Math.floor(totalMinutes)
  const ss = Math.round((totalMinutes - mm) * 60)
  return `${mm}:${ss.toString().padStart(2, '0')}`
}

const fmtMinutesFromHms = (hms) => {
  if (!hms) return '00:00'
  const [hh, mm, ss] = hms.split(':')
  const totalMinutes = parseInt(hh, 10) * 60 + parseInt(mm, 10)
  return `${totalMinutes}:${(ss || '00').split('.')[0]}`
}

const fmtAvgMinutes = (avgMinutes) => {
  if (avgMinutes === undefined || avgMinutes === null) return '-'
  const mm = Math.floor(avgMinutes)
  const ss = Math.round((avgMinutes - mm) * 60)
  return `${mm}:${ss.toString().padStart(2, '0')}`
}

const fmtRatio = (made, attempted) => {
  if ((made === 0 || made === undefined) && (attempted === 0 || attempted === undefined)) return '-'
  return `${made ?? 0}/${attempted ?? 0}`
}

const fmtPct = (made, attempted) => {
  if (!attempted) return '-'
  const pct = (made / attempted) * 100
  return `${pct.toFixed(1)}%`
}

const fmtNum = (val) => (val === undefined || val === null ? '-' : val)
const fmtFloat = (val) => (val === undefined || val === null ? '-' : Number(val).toFixed(1))

// total: PlayerStatsTotals row (e.g. season aggregate, career aggregate)
export const totalCells = (s = {}) => ({
  gamesPlayed: fmtNum(s.gamesPlayed),
  gamesStarted: fmtNum(s.gamesStarted),
  minutes: fmtMinutesFromTotal(s.minutes),
  points: fmtNum(s.points),
  fg2: fmtRatio(s.made2Pointers, s.attempted2Pointers),
  fg3: fmtRatio(s.made3Pointers, s.attempted3Pointers),
  ft: fmtRatio(s.made1Pointers, s.attempted1Pointers),
  oReb: fmtNum(s.offensiveRebounds),
  dReb: fmtNum(s.defensiveRebounds),
  tReb: fmtNum(s.totalRebounds),
  assists: fmtNum(s.assists),
  steals: fmtNum(s.steals),
  turnovers: fmtNum(s.turnovers),
  blocksFv: fmtNum(s.blocks),
  blocksAg: fmtNum(s.blocksAgainst),
  foulsCm: fmtNum(s.fouls),
  foulsRv: fmtNum(s.foulsDrawn),
  pir: fmtNum(s.pir),
})

// avg: PlayerStatsAverages row. Shooting cells become percentages.
export const averageCells = (a = {}) => ({
  gamesPlayed: fmtNum(a.gamesPlayed),
  gamesStarted: '-',
  minutes: fmtAvgMinutes(a.minutes),
  points: fmtFloat(a.points),
  fg2: fmtPct(a.made2Pointers, a.attempted2Pointers),
  fg3: fmtPct(a.made3Pointers, a.attempted3Pointers),
  ft: fmtPct(a.made1Pointers, a.attempted1Pointers),
  oReb: fmtFloat(a.offensiveRebounds),
  dReb: fmtFloat(a.defensiveRebounds),
  tReb: fmtFloat(a.totalRebounds),
  assists: fmtFloat(a.assists),
  steals: fmtFloat(a.steals),
  turnovers: fmtFloat(a.turnovers),
  blocksFv: fmtFloat(a.blocks),
  blocksAg: fmtFloat(a.blocksAgainst),
  foulsCm: fmtFloat(a.fouls),
  foulsRv: fmtFloat(a.foulsDrawn),
  pir: fmtFloat(a.pir),
})

// game log: legacy /stats/players/{id} returns minutes as "HH:MM:SS.fff",
// shooting as raw made/attempted.
export const gameCells = (s = {}) => ({
  minutes: fmtMinutesFromHms(s.minutes),
  points: fmtNum(s.points),
  fg2: fmtRatio(s.made2Pointers, s.attempted2Pointers),
  fg3: fmtRatio(s.made3Pointers, s.attempted3Pointers),
  ft: fmtRatio(s.made1Pointers, s.attempted1Pointers),
  oReb: fmtNum(s.offensiveRebounds),
  dReb: fmtNum(s.defensiveRebounds),
  tReb: fmtNum((s.offensiveRebounds ?? 0) + (s.defensiveRebounds ?? 0)),
  assists: fmtNum(s.assists),
  steals: fmtNum(s.steals),
  turnovers: fmtNum(s.turnovers),
  blocksFv: fmtNum(s.blocks),
  blocksAg: fmtNum(s.blocksAgainst),
  foulsCm: fmtNum(s.fouls),
  foulsRv: fmtNum(s.foulsDrawn),
  pir: fmtNum(s.pir),
})

// Column groups for the season/career tables (no game-log column).
// `key` = leaf id matching the *Cells objects above. `label` = column header.
// Groups with `label` get a spanning header row above their children.
export const STATS_GROUPS = [
  { children: [
    { key: 'gamesPlayed', label: 'G' },
    { key: 'gamesStarted', label: 'GS' },
    { key: 'minutes', label: 'Min' },
    { key: 'points', label: 'Pts' },
    { key: 'fg2', label: '2FG' },
    { key: 'fg3', label: '3FG' },
    { key: 'ft', label: 'FT' },
  ] },
  { label: 'Rebounds', children: [
    { key: 'oReb', label: 'O' },
    { key: 'dReb', label: 'D' },
    { key: 'tReb', label: 'T' },
  ] },
  { children: [
    { key: 'assists', label: 'As' },
    { key: 'steals', label: 'St' },
    { key: 'turnovers', label: 'To' },
  ] },
  { label: 'Blocks', children: [
    { key: 'blocksFv', label: 'Fv' },
    { key: 'blocksAg', label: 'Ag' },
  ] },
  { label: 'Fouls', children: [
    { key: 'foulsCm', label: 'Cm' },
    { key: 'foulsRv', label: 'Rv' },
  ] },
  { children: [
    { key: 'pir', label: 'PIR' },
  ] },
]

// Game log uses the same shape but without G/GS columns.
export const GAME_LOG_GROUPS = [
  { children: [
    { key: 'minutes', label: 'Min' },
    { key: 'points', label: 'Pts' },
    { key: 'fg2', label: '2FG' },
    { key: 'fg3', label: '3FG' },
    { key: 'ft', label: 'FT' },
  ] },
  { label: 'Rebounds', children: [
    { key: 'oReb', label: 'O' },
    { key: 'dReb', label: 'D' },
    { key: 'tReb', label: 'T' },
  ] },
  { children: [
    { key: 'assists', label: 'As' },
    { key: 'steals', label: 'St' },
    { key: 'turnovers', label: 'To' },
  ] },
  { label: 'Blocks', children: [
    { key: 'blocksFv', label: 'Fv' },
    { key: 'blocksAg', label: 'Ag' },
  ] },
  { label: 'Fouls', children: [
    { key: 'foulsCm', label: 'Cm' },
    { key: 'foulsRv', label: 'Rv' },
  ] },
  { children: [
    { key: 'pir', label: 'PIR' },
  ] },
]

export const flattenLeaves = (groups) =>
  groups.flatMap((g, gi) =>
    g.children.map((child, ci) => ({
      ...child,
      firstOfGroup: ci === 0 && gi > 0,
    })),
  )
